import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';
import AppShell from '../components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BAND_DOT = {
  strong: 'bg-forest',
  steady: 'bg-olive',
  mixed: 'bg-sand',
  worth_watching: 'bg-clay',
  needs_attention: 'bg-clay-soft',
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  } catch { return iso; }
};

const MyLife = () => {
  const [user, setUser] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [me, latest, tl] = await Promise.all([
          axios.get(`${API}/auth/me`),
          axios.get(`${API}/assessment/latest`).catch(() => ({ data: { assessment: null } })),
          axios.get(`${API}/timeline`).catch(() => ({ data: { events: [] } })),
        ]);
        if (cancelled) return;
        setUser(me.data);
        setAssessment(latest.data?.assessment || null);
        setEvents(tl.data?.events || []);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const firstName = user?.name ? user.name.split(' ')[0] : 'there';
  const hasProfile = !!assessment?.profile;
  const recentEvents = events.slice(0, 3);

  return (
    <AppShell>
      <SEO title="My Life" description="Your personal Summaros space." path="/my-life" noindex />
      <section className="max-w-5xl mx-auto px-6 sm:px-8 py-16 sm:py-20 animate-fade-up" data-testid="my-life-page">
        <div className="mb-14">
          <p className="eyebrow mb-4">My Life</p>
          <h1
            className="display-serif text-4xl sm:text-5xl text-forest text-balance"
            style={{ fontVariationSettings: "'opsz' 96, 'SOFT' 50, 'wght' 360" }}
            data-testid="my-life-greeting"
          >
            Hey, {firstName}. Here's your life lately.
          </h1>
        </div>

        {!loaded ? (
          <div className="h-40" />
        ) : (
        <div className="grid md:grid-cols-3 gap-5">
          {/* ── Life Profile card ─────────────────── */}
          <div className="md:col-span-2 card bg-cream-soft border-line-strong" data-testid="my-life-profile-card">
            <div className="flex items-center justify-between mb-4">
              <p className="eyebrow">Life Profile</p>
              {hasProfile && (
                <Link to="/life-profile" className="text-[13px] text-forest hover:text-clay inline-flex items-center gap-1">
                  Open <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                </Link>
              )}
            </div>

            {!hasProfile ? (
              <div className="grid sm:grid-cols-[1fr,180px] gap-6 items-center">
                <div>
                  <h2
                    className="display-serif text-2xl sm:text-3xl text-forest mb-3"
                    style={{ fontVariationSettings: "'opsz' 48, 'SOFT' 40, 'wght' 420" }}
                  >
                    We don't know you yet.
                  </h2>
                  <p className="text-ink-muted mb-6 leading-relaxed max-w-md">
                    Take the Life Assessment and we'll show you a small picture of how the parts of your life are going, and how they might be connected.
                  </p>
                  <Link to="/assess" className="btn-primary" data-testid="my-life-cta-assess">
                    Start my Life Assessment <ArrowRight className="w-4 h-4" strokeWidth={2} />
                  </Link>
                </div>
                <img
                  src="/art/wheel.png"
                  alt=""
                  draggable={false}
                  aria-hidden="true"
                  className="hidden sm:block w-full h-auto select-none opacity-95"
                  data-testid="my-life-empty-art"
                />
              </div>
            ) : (
              <>
                <h2
                  className="display-serif text-2xl sm:text-3xl text-forest mb-5"
                  style={{ fontVariationSettings: "'opsz' 48, 'SOFT' 40, 'wght' 420" }}
                >
                  This is you, roughly.
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {assessment.profile.domains.map((d) => (
                    <div key={d.id} className="rounded-lg border border-line px-3 py-2.5 bg-cream" data-testid={`my-life-domain-${d.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${BAND_DOT[d.band] || 'bg-sand'}`} />
                        <span className="text-[11px] font-mono uppercase tracking-widest text-ink-faint">{d.band_label}</span>
                      </div>
                      <p className="text-[13px] text-forest font-medium leading-tight">{d.title}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* ── Timeline card ─────────────────────── */}
          <div className="card" data-testid="my-life-timeline-card">
            <div className="flex items-center justify-between mb-4">
              <p className="eyebrow">Timeline</p>
              <Link to="/timeline" className="text-[13px] text-forest hover:text-clay inline-flex items-center gap-1">
                Open <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
              </Link>
            </div>
            {recentEvents.length === 0 ? (
              <>
                <p className="text-[15px] text-ink-muted leading-relaxed mb-4">Life happened. Add it.</p>
                <Link to="/timeline" className="text-forest hover:text-clay text-sm font-medium inline-flex items-center gap-1.5" data-testid="my-life-cta-timeline">
                  Add an event <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                </Link>
              </>
            ) : (
              <ul className="space-y-3">
                {recentEvents.map((ev) => (
                  <li key={ev.event_id} className="text-[14px]">
                    <p className="text-forest font-medium leading-snug">{ev.title}</p>
                    <p className="text-[12px] text-ink-faint mt-0.5">{formatDate(ev.date)}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── Community card ────────────────────── */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <p className="eyebrow">Community</p>
              <Link to="/community" className="text-[13px] text-forest hover:text-clay inline-flex items-center gap-1">
                Open <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
              </Link>
            </div>
            <p className="text-[15px] text-ink-muted leading-relaxed">
              Noticed anything about being human lately?
            </p>
          </div>

          {/* ── Ask Summaros card ─────────────────── */}
          <div className="card md:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <p className="eyebrow">Ask Summaros</p>
              <Link to="/ask" className="text-[13px] text-forest hover:text-clay inline-flex items-center gap-1">
                Open <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
              </Link>
            </div>
            <p className="text-[15px] text-ink-muted leading-relaxed">
              {hasProfile
                ? "What should I focus on? Where am I doing well? What might I be overlooking?"
                : "Once you've done your assessment, ask away."}
            </p>
          </div>
        </div>
        )}
      </section>
    </AppShell>
  );
};

export default MyLife;
